import { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import "../../../assets/seleccionMasiva/carga-nomina-inhabilitados.scss";
import { checkRut, formatRut } from "../../../hooks/useRut";
import {
  ClientOption,
  ExclusionType,
  InhabilitadoCargaResult,
  fetchClients,
  fetchExclusionTypes,
  processInhabilitadosNomina,
} from "../../../services/inhabilitadosNominaService";
import {
  downloadInhabilitadosResult,
  isClientLevelType,
  normalizeRutKey,
  parsePastedRows,
  toUpperComment,
  toUpperName,
} from "./inhabilitadosCargaResult";
import SearchSelect, { SearchSelectOption } from "./SearchSelect";
import MultiSearchSelect from "./MultiSearchSelect";

interface CargaManualInhabilitadosProps {
  visible: boolean;
  onHide: () => void;
  onLoaded?: () => void;
}

// Carga manual de inhabilitados: funciona igual que la carga por nómina Excel,
// pero en lugar de subir un archivo, el usuario escribe/pega los registros
// directamente. Comparte el mismo endpoint (inhabilitados/carga) y la misma
// pantalla de asignación (tipo, cliente, comentario) y de resultado.

type ManualStep = "ingreso" | "asignacion" | "result";

interface AssignmentRow {
  rutRaw: string;
  rutFormateado: string;
  rutValido: boolean;
  nombres: string;
  apellidos: string;
  excTipoId: string;
  clienteIds: string[];
  comentario: string;
}

const PAGE_SIZE = 50;

const buildAssignmentRow = (
  rut: string,
  nombres: string,
  apellidos: string
): AssignmentRow => {
  const rutRaw = rut.trim();
  const rutValido = checkRut(rutRaw);
  return {
    rutRaw,
    rutFormateado: rutValido ? formatRut(rutRaw).toUpperCase() : rutRaw.toUpperCase(),
    rutValido,
    nombres: toUpperName(nombres),
    apellidos: toUpperName(apellidos),
    excTipoId: "",
    clienteIds: [],
    comentario: "",
  };
};

const CargaManualInhabilitados = ({
  visible,
  onHide,
  onLoaded,
}: CargaManualInhabilitadosProps) => {
  const [step, setStep] = useState<ManualStep>("ingreso");

  const [pasteText, setPasteText] = useState("");
  const [rows, setRows] = useState<AssignmentRow[]>([]);

  const [types, setTypes] = useState<ExclusionType[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [catalogsError, setCatalogsError] = useState("");

  const [applyToAll, setApplyToAll] = useState(true);
  const [bulkTipo, setBulkTipo] = useState("");
  const [bulkClientes, setBulkClientes] = useState<string[]>([]);
  const [bulkComentario, setBulkComentario] = useState("");
  const [assignPage, setAssignPage] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<InhabilitadoCargaResult>();
  const [resultFilter, setResultFilter] = useState("all");
  const [resultPage, setResultPage] = useState(0);

  const typesById = useMemo(
    () => new Map(types.map((tipo) => [tipo.excTipoId, tipo])),
    [types]
  );

  const typeSelectOptions = useMemo<SearchSelectOption[]>(
    () =>
      types
        .filter((tipo) =>
          ["all", "cliente"].includes((tipo.exclusionLevel ?? "").toLowerCase())
        )
        .map((tipo) => ({ code: tipo.excTipoId, name: tipo.excTipoNombre })),
    [types]
  );
  const clientSelectOptions = useMemo<SearchSelectOption[]>(
    () =>
      clients.map((cliente) => ({
        code: cliente.clienteId,
        name: cliente.clienteDni
          ? `${cliente.clienteNombre} (${cliente.clienteDni})`
          : cliente.clienteNombre,
      })),
    [clients]
  );

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        setCatalogsError("");
        const [tiposResult, clientesResult] = await Promise.all([
          fetchExclusionTypes(),
          fetchClients(),
        ]);
        if (cancelled) return;
        setTypes(
          [...tiposResult].sort((a, b) =>
            (a.excTipoNombre ?? "").localeCompare(b.excTipoNombre ?? "")
          )
        );
        setClients(
          [...clientesResult].sort((a, b) =>
            (a.clienteNombre ?? "").localeCompare(b.clienteNombre ?? "")
          )
        );
      } catch (_) {
        if (!cancelled)
          setCatalogsError(
            "No fue posible cargar los tipos de inhabilitación o los clientes."
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const reset = () => {
    setStep("ingreso");
    setPasteText("");
    setRows([]);
    setApplyToAll(true);
    setBulkTipo("");
    setBulkClientes([]);
    setBulkComentario("");
    setAssignPage(0);
    setLoading(false);
    setError("");
    setResult(undefined);
    setResultFilter("all");
    setResultPage(0);
  };

  const close = () => {
    reset();
    onHide();
  };

  // --- Paso ingreso ---------------------------------------------------------
  const addPastedRows = () => {
    const parsed = parsePastedRows(pasteText);
    if (parsed.length === 0) return;
    setRows((current) => {
      const seen = new Set(current.map((row) => normalizeRutKey(row.rutRaw)));
      const next = [...current];
      parsed.forEach(({ rut, nombres, apellidos }) => {
        const key = normalizeRutKey(rut);
        if (key && seen.has(key)) return;
        seen.add(key);
        next.push(buildAssignmentRow(rut, nombres, apellidos));
      });
      return next;
    });
    setPasteText("");
  };

  const addEmptyRow = () => {
    setRows((current) => [...current, buildAssignmentRow("", "", "")]);
  };

  const updateIngresoRow = (
    index: number,
    patch: Partial<Pick<AssignmentRow, "rutRaw" | "nombres" | "apellidos">>
  ) => {
    setRows((current) =>
      current.map((row, position) => {
        if (position !== index) return row;
        const rutRaw = patch.rutRaw !== undefined ? patch.rutRaw : row.rutRaw;
        const rutValido = checkRut(rutRaw);
        return {
          ...row,
          ...patch,
          rutRaw,
          rutValido,
          rutFormateado: rutValido
            ? formatRut(rutRaw).toUpperCase()
            : rutRaw.toUpperCase(),
        };
      })
    );
  };

  const removeRow = (index: number) => {
    setRows((current) => current.filter((_, position) => position !== index));
  };

  const ingresoValidCount = rows.filter((row) => row.rutValido).length;
  const ingresoInvalidCount = rows.length - ingresoValidCount;
  const canContinue = rows.length > 0 && ingresoValidCount > 0 && !catalogsError;

  const goToAssignment = () => {
    setError("");
    setApplyToAll(true);
    setAssignPage(0);
    setStep("asignacion");
  };

  // --- Paso asignación (igual que la carga por nómina) ----------------------
  useEffect(() => {
    if (step !== "asignacion" || !applyToAll) return;
    const clientesForBulk = isClientLevelType(bulkTipo, typesById)
      ? bulkClientes
      : [];
    setRows((current) =>
      current.map((row) => ({
        ...row,
        excTipoId: bulkTipo,
        clienteIds: clientesForBulk,
        comentario: bulkComentario !== "" ? bulkComentario : row.comentario,
      }))
    );
  }, [applyToAll, bulkTipo, bulkClientes, bulkComentario, step, typesById]);

  const updateRow = (index: number, patch: Partial<AssignmentRow>) => {
    setRows((current) =>
      current.map((row, position) => {
        if (position !== index) return row;
        const next = { ...row, ...patch };
        if (
          patch.excTipoId !== undefined &&
          !isClientLevelType(next.excTipoId, typesById)
        ) {
          next.clienteIds = [];
        }
        return next;
      })
    );
  };

  const validRows = rows.filter((row) => row.rutValido);
  const invalidRutCount = rows.length - validRows.length;
  const rowReady = (row: AssignmentRow): boolean => {
    if (!row.rutValido) return true; // se rechaza en backend, no bloquea el envío
    if (!row.excTipoId) return false;
    if (!row.comentario.trim()) return false;
    if (
      isClientLevelType(row.excTipoId, typesById) &&
      row.clienteIds.length === 0
    )
      return false;
    return true;
  };
  const pendingRows = rows.filter((row) => !rowReady(row)).length;
  const canProcess = validRows.length > 0 && pendingRows === 0 && !catalogsError;

  const process = async () => {
    if (!canProcess) return;
    setLoading(true);
    setError("");
    try {
      const registros = rows.map((row) => ({
        rut: row.rutRaw,
        nombres: toUpperName(row.nombres),
        apellidos: toUpperName(row.apellidos),
        excTipoId: row.excTipoId,
        clienteIds: isClientLevelType(row.excTipoId, typesById)
          ? row.clienteIds
          : [],
        comentario: toUpperComment(row.comentario),
      }));
      const response = await processInhabilitadosNomina({
        fileName: "Carga manual",
        sheetName: "",
        registros,
      });
      setResult(response);
      setResultFilter("all");
      setResultPage(0);
      setStep("result");
      onLoaded && onLoaded();
    } catch (processError: any) {
      setError(
        processError?.response?.data?.message ??
          processError?.message ??
          "No fue posible cargar los registros de inhabilitados."
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadResult = () => {
    if (!result) return;
    downloadInhabilitadosResult(result, "inhabilitados_manual");
  };

  // --- Paginación tabla de asignación ---
  const assignTotalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const assignStart = assignPage * PAGE_SIZE;
  const assignRows = rows.slice(assignStart, assignStart + PAGE_SIZE);

  // --- Resultado ---
  const detalle = result?.detalle ?? [];
  const filteredDetalle =
    resultFilter === "all"
      ? detalle
      : detalle.filter((item) => item.estado === resultFilter);
  const resultTotalPages = Math.max(
    1,
    Math.ceil(filteredDetalle.length / PAGE_SIZE)
  );
  const resultStart = resultPage * PAGE_SIZE;
  const resultRows = filteredDetalle.slice(resultStart, resultStart + PAGE_SIZE);

  const selectResultFilter = (filter: string) => {
    setResultFilter(filter);
    setResultPage(0);
  };

  const statusModifier = (value: string): string =>
    (value ?? "").toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

  const renderHeader = () => (
    <div className="inhab-nomina__titlebar">
      <div>
        <span className="inhab-nomina__eyebrow">Carga manual</span>
        <h2>Cargar Inhabilitados manualmente</h2>
      </div>
      <button
        type="button"
        className="inhab-nomina__icon-button"
        onClick={close}
        aria-label="Cerrar"
      >
        <CloseIcon />
      </button>
    </div>
  );

  return (
    <Dialog
      header={renderHeader}
      visible={visible}
      onHide={close}
      closable={false}
      draggable={false}
      modal
      className="inhab-nomina"
      style={{ width: "min(1180px, 96vw)", maxHeight: "calc(100vh - 24px)" }}
      contentStyle={{ padding: 0 }}
    >
      <div className="inhab-nomina__steps" aria-label="Progreso">
        {["Ingresar RUTs", "Inhabilitación", "Resultado"].map((label, index) => {
          const activeIndex =
            step === "ingreso" ? 0 : step === "asignacion" ? 1 : 2;
          return (
            <div
              className={`inhab-nomina__step ${
                index <= activeIndex ? "is-active" : ""
              }`}
              key={label}
            >
              <span>{index + 1}</span>
              {label}
            </div>
          );
        })}
      </div>

      <div className="inhab-nomina__body">
        {catalogsError && (
          <div className="inhab-nomina__alert">
            <ErrorOutlineIcon />
            {catalogsError}
          </div>
        )}
        {error && (
          <div className="inhab-nomina__alert">
            <ErrorOutlineIcon />
            {error}
          </div>
        )}

        {step === "ingreso" && (
          <section className="inhab-nomina__assign">
            <div className="inhab-nomina__bulk is-on">
              <div className="inhab-nomina__bulk-head">
                <span className="inhab-nomina__switch-label">
                  Pegar o escribir registros
                </span>
                <small>
                  Un registro por línea. Puedes pegar solo el RUT, o
                  &quot;RUT, Nombres, Apellidos&quot; (separados por coma, punto y
                  coma o tabulación). El nombre es opcional.
                </small>
              </div>
              <div className="inhab-nomina__bulk-controls" style={{ gridTemplateColumns: "1fr auto" }}>
                <label className="inhab-nomina__bulk-comment">
                  <span>Registros</span>
                  <textarea
                    value={pasteText}
                    onChange={(event) => setPasteText(event.target.value)}
                    rows={5}
                    placeholder={"11111111-1\n22222222-2, JUAN, PEREZ\n33333333-3; MARIA; SOTO"}
                    style={{
                      width: "100%",
                      resize: "vertical",
                      fontFamily: "inherit",
                      fontSize: "13px",
                      padding: "10px 12px",
                      border: "1px solid #cdd7e5",
                      borderRadius: "10px",
                      lineHeight: 1.5,
                    }}
                  />
                </label>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    type="button"
                    className="inhab-nomina__primary"
                    style={{ whiteSpace: "nowrap" }}
                    disabled={parsePastedRows(pasteText).length === 0}
                    onClick={addPastedRows}
                  >
                    <AddRoundedIcon /> Agregar a la lista
                  </button>
                </div>
              </div>
            </div>

            <div className="inhab-nomina__assign-heading">
              <div>
                <h3>Registros a inhabilitar</h3>
                <p>
                  Revisa los RUT. En el siguiente paso asignas el tipo, cliente
                  y comentario.
                </p>
              </div>
              <div className="inhab-nomina__assign-counters">
                <span className="ok">{ingresoValidCount} válidos</span>
                {ingresoInvalidCount > 0 && (
                  <span className="bad">{ingresoInvalidCount} RUT inválido</span>
                )}
                <button
                  type="button"
                  className="inhab-nomina__secondary"
                  onClick={addEmptyRow}
                >
                  <AddRoundedIcon /> Agregar fila
                </button>
              </div>
            </div>

            <div className="inhab-nomina__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>RUT</th>
                    <th>Nombres</th>
                    <th>Apellidos</th>
                    <th aria-label="Quitar"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td className="inhab-nomina__empty-result" colSpan={5}>
                        Aún no hay registros. Pega o escribe RUTs arriba y
                        presiona &quot;Agregar a la lista&quot;.
                      </td>
                    </tr>
                  )}
                  {rows.map((row, index) => (
                    <tr
                      key={`${row.rutRaw}-${index}`}
                      className={!row.rutValido && row.rutRaw ? "is-invalid" : ""}
                    >
                      <td>{index + 1}</td>
                      <td>
                        <input
                          value={row.rutRaw}
                          maxLength={13}
                          placeholder="11111111-1"
                          onChange={(event) =>
                            updateIngresoRow(index, { rutRaw: event.target.value })
                          }
                        />
                        {!row.rutValido && row.rutRaw && (
                          <span className="inhab-nomina__rut-flag">
                            RUT inválido
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          value={row.nombres}
                          maxLength={100}
                          placeholder="Nombres"
                          onChange={(event) =>
                            updateIngresoRow(index, {
                              nombres: event.target.value.toUpperCase(),
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={row.apellidos}
                          maxLength={100}
                          placeholder="Apellidos"
                          onChange={(event) =>
                            updateIngresoRow(index, {
                              apellidos: event.target.value.toUpperCase(),
                            })
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="inhab-nomina__icon-button"
                          aria-label={`Quitar fila ${index + 1}`}
                          onClick={() => removeRow(index)}
                        >
                          <DeleteOutlineRoundedIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {step === "asignacion" && (
          <section className="inhab-nomina__assign">
            <div className={`inhab-nomina__bulk ${applyToAll ? "is-on" : ""}`}>
              <div className="inhab-nomina__bulk-head">
                <label className="inhab-nomina__switch">
                  <input
                    type="checkbox"
                    checked={applyToAll}
                    onChange={(event) => setApplyToAll(event.target.checked)}
                  />
                  <span className="inhab-nomina__switch-track">
                    <span className="inhab-nomina__switch-thumb" />
                  </span>
                  <span className="inhab-nomina__switch-label">
                    Aplicar a todos
                  </span>
                </label>
                <small>
                  {applyToAll
                    ? "El tipo, cliente y comentario definidos aquí se aplican a todos los registros."
                    : "Configura el tipo, cliente y comentario en cada registro de la tabla."}
                </small>
              </div>
              {applyToAll && (
                <div className="inhab-nomina__bulk-controls">
                  <label>
                    <span>Tipo de inhabilitación</span>
                    <SearchSelect
                      value={bulkTipo}
                      options={typeSelectOptions}
                      onChange={(code) => {
                        setBulkTipo(code);
                        if (!isClientLevelType(code, typesById))
                          setBulkClientes([]);
                      }}
                      placeholder="Seleccionar"
                      searchPlaceholder="Buscar tipo"
                      ariaLabel="Tipo de inhabilitación"
                    />
                  </label>
                  {isClientLevelType(bulkTipo, typesById) && (
                    <label>
                      <span>Cliente(s)</span>
                      <MultiSearchSelect
                        value={bulkClientes}
                        options={clientSelectOptions}
                        onChange={(codes) => setBulkClientes(codes)}
                        placeholder="Seleccionar"
                        searchPlaceholder="Buscar cliente"
                        ariaLabel="Clientes"
                      />
                    </label>
                  )}
                  <label className="inhab-nomina__bulk-comment">
                    <span>Comentario</span>
                    <input
                      value={bulkComentario}
                      onChange={(event) =>
                        setBulkComentario(event.target.value.toUpperCase())
                      }
                      maxLength={300}
                      placeholder="Motivo de la inhabilitación"
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="inhab-nomina__assign-heading">
              <div>
                <h3>Registros a inhabilitar</h3>
                <p>
                  Asigna el tipo de inhabilitación, cliente (si corresponde) y
                  comentario. El comentario es obligatorio.
                </p>
              </div>
              <div className="inhab-nomina__assign-counters">
                <span className="ok">{validRows.length} válidos</span>
                {invalidRutCount > 0 && (
                  <span className="bad">{invalidRutCount} RUT inválido</span>
                )}
                {pendingRows > 0 && (
                  <span className="warn">{pendingRows} sin completar</span>
                )}
              </div>
            </div>

            <div className="inhab-nomina__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>RUT</th>
                    <th>Nombre</th>
                    <th>Tipo de inhabilitación</th>
                    <th>Cliente</th>
                    <th>Comentario</th>
                  </tr>
                </thead>
                <tbody>
                  {assignRows.map((row, position) => {
                    const index = assignStart + position;
                    const clientLevel = isClientLevelType(
                      row.excTipoId,
                      typesById
                    );
                    return (
                      <tr
                        key={`${row.rutRaw}-${index}`}
                        className={!row.rutValido ? "is-invalid" : ""}
                      >
                        <td>{index + 1}</td>
                        <td>
                          {row.rutFormateado || <em>Sin dato</em>}
                          {!row.rutValido && (
                            <span className="inhab-nomina__rut-flag">
                              RUT inválido
                            </span>
                          )}
                        </td>
                        <td>
                          {`${row.nombres} ${row.apellidos}`.trim() || (
                            <em>Sin dato</em>
                          )}
                        </td>
                        <td>
                          <SearchSelect
                            value={row.excTipoId}
                            options={typeSelectOptions}
                            disabled={applyToAll || !row.rutValido}
                            onChange={(code) =>
                              updateRow(index, { excTipoId: code })
                            }
                            placeholder="Seleccionar"
                            searchPlaceholder="Buscar tipo"
                            ariaLabel="Tipo de inhabilitación"
                          />
                        </td>
                        <td>
                          {clientLevel ? (
                            <MultiSearchSelect
                              value={row.clienteIds}
                              options={clientSelectOptions}
                              disabled={applyToAll || !row.rutValido}
                              onChange={(codes) =>
                                updateRow(index, { clienteIds: codes })
                              }
                              placeholder="Seleccionar"
                              searchPlaceholder="Buscar cliente"
                              ariaLabel="Clientes"
                            />
                          ) : (
                            <span className="inhab-nomina__na">No aplica</span>
                          )}
                        </td>
                        <td>
                          <input
                            value={row.comentario}
                            disabled={applyToAll || !row.rutValido}
                            maxLength={300}
                            placeholder="Comentario"
                            onChange={(event) =>
                              updateRow(index, {
                                comentario: event.target.value.toUpperCase(),
                              })
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="inhab-nomina__pagination">
              <span>
                Mostrando {rows.length ? assignStart + 1 : 0}–
                {Math.min(assignStart + PAGE_SIZE, rows.length)} de{" "}
                {rows.length.toLocaleString("es-CL")}
              </span>
              <div>
                <button
                  type="button"
                  disabled={assignPage === 0}
                  onClick={() => setAssignPage((page) => Math.max(0, page - 1))}
                >
                  Anterior
                </button>
                <strong>
                  {assignPage + 1} / {assignTotalPages}
                </strong>
                <button
                  type="button"
                  disabled={assignPage + 1 >= assignTotalPages}
                  onClick={() =>
                    setAssignPage((page) =>
                      Math.min(assignTotalPages - 1, page + 1)
                    )
                  }
                >
                  Siguiente
                </button>
              </div>
            </div>
          </section>
        )}

        {step === "result" && result && (
          <section className="inhab-nomina__result">
            <div className="inhab-nomina__result-heading">
              <div className="inhab-nomina__result-icon">
                <PersonRemoveOutlinedIcon />
              </div>
              <div>
                <h3>Registros procesados</h3>
                <p>{result.message}</p>
              </div>
            </div>
            <div className="inhab-nomina__summary">
              <button
                type="button"
                className={`inhab-nomina__summary-card ${
                  resultFilter === "all" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("all")}
              >
                <strong>{result.resumen.totalRegistros}</strong>
                <span>Total</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card ok ${
                  resultFilter === "CREADO" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("CREADO")}
              >
                <strong>{result.resumen.creados}</strong>
                <span>Inhabilitados</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card info ${
                  resultFilter === "personas" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("all")}
              >
                <strong>{result.resumen.personasCreadas}</strong>
                <span>Personas creadas</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card warn ${
                  resultFilter === "DUPLICADO" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("DUPLICADO")}
              >
                <strong>{result.resumen.duplicados}</strong>
                <span>Duplicados</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card bad ${
                  resultFilter === "RUT_INVALIDO" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("RUT_INVALIDO")}
              >
                <strong>{result.resumen.rutInvalidos}</strong>
                <span>RUT inválido</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card bad ${
                  resultFilter === "ERROR" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("ERROR")}
              >
                <strong>
                  {result.resumen.errores +
                    result.resumen.tipoInvalidos +
                    result.resumen.clienteRequerido +
                    result.resumen.clienteInvalidos +
                    result.resumen.comentarioRequerido}
                </strong>
                <span>Con problemas</span>
              </button>
            </div>

            <div className="inhab-nomina__table-wrap inhab-nomina__result-table">
              <table>
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>RUT</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {resultRows.map((item) => (
                    <tr key={`${item.fila}-${item.rutNormalizado}`}>
                      <td>{item.fila}</td>
                      <td>
                        {item.rutNormalizado || item.rut || <em>Sin dato</em>}
                      </td>
                      <td>{item.nombre || <em>Sin dato</em>}</td>
                      <td>
                        {item.tipo || <em>—</em>}
                        {item.personaCreada && (
                          <span className="inhab-nomina__tag">Persona nueva</span>
                        )}
                      </td>
                      <td>{item.cliente || <em>—</em>}</td>
                      <td
                        className={`inhab-nomina__status--${statusModifier(
                          item.estado
                        )}`}
                      >
                        {item.estado}
                      </td>
                      <td>{item.mensaje || <em>—</em>}</td>
                    </tr>
                  ))}
                  {resultRows.length === 0 && (
                    <tr>
                      <td className="inhab-nomina__empty-result" colSpan={7}>
                        No hay registros para este filtro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="inhab-nomina__pagination">
              <span>
                Mostrando {filteredDetalle.length ? resultStart + 1 : 0}–
                {Math.min(resultStart + PAGE_SIZE, filteredDetalle.length)} de{" "}
                {filteredDetalle.length.toLocaleString("es-CL")}
              </span>
              <div>
                <button
                  type="button"
                  disabled={resultPage === 0}
                  onClick={() => setResultPage((page) => Math.max(0, page - 1))}
                >
                  Anterior
                </button>
                <strong>
                  {resultPage + 1} / {resultTotalPages}
                </strong>
                <button
                  type="button"
                  disabled={resultPage + 1 >= resultTotalPages}
                  onClick={() =>
                    setResultPage((page) =>
                      Math.min(resultTotalPages - 1, page + 1)
                    )
                  }
                >
                  Siguiente
                </button>
              </div>
            </div>
          </section>
        )}
      </div>

      <footer className="inhab-nomina__footer">
        {step === "ingreso" && (
          <>
            <button
              type="button"
              className="inhab-nomina__secondary"
              onClick={close}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="inhab-nomina__primary"
              disabled={!canContinue}
              onClick={goToAssignment}
            >
              Continuar
            </button>
          </>
        )}
        {step === "asignacion" && (
          <>
            <button
              type="button"
              className="inhab-nomina__secondary"
              onClick={() => setStep("ingreso")}
            >
              <ArrowBackIcon />
              Volver
            </button>
            <button
              type="button"
              className="inhab-nomina__primary"
              disabled={!canProcess || loading}
              onClick={process}
            >
              {loading ? "Procesando…" : "Inhabilitar registros"}
            </button>
          </>
        )}
        {step === "result" && (
          <>
            <button
              type="button"
              className="inhab-nomina__excel"
              onClick={downloadResult}
            >
              <FileDownloadOutlinedIcon />
              Descargar resultados
            </button>
            <button
              type="button"
              className="inhab-nomina__primary"
              onClick={close}
            >
              Cerrar
            </button>
          </>
        )}
      </footer>
    </Dialog>
  );
};

export default CargaManualInhabilitados;
